/**
 * Wallet Expenses Repository
 *
 * Handles CRUD operations and real-time listeners for expense documents
 * stored at users/{uid}/wallet/expenses/{expenseId}
 *
 * Records are immutable (write-once) - no update method provided.
 */

import {
  DocumentSnapshot,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  deleteDoc,
  where,
} from 'firebase/firestore';
import { Paths, Page, PaginationOptions, withBackoff, setDoc, getDoc } from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import type { Expense, ExpenseStatus } from '../types/expenses';
import {
  ExpenseDoc,
  toExpenseDoc,
  fromExpenseDoc,
  generateWalletItemId,
} from '../types/walletFirestore';
import { shouldTagAsTest } from '../config/agenticWorkflowConfig';

// ============================================================================
// Types
// ============================================================================

export interface CreateExpenseInput {
  /** Summary description */
  summary: string;
  /** Member ID */
  memberId: string;
  /** Amount in USD */
  amountUsd: number;
  /** Amount in OLV (snapshot) */
  amountOlv: number;
  /** OLV rate at time of transaction */
  olvRateAtTime: number;
  /** Tags for categorization */
  tags?: string[];
  /** AI model ID (optional) */
  modelId?: string;
  /** Provider name (optional) */
  provider?: string;
  /** Initial status (defaults to 'completed') */
  status?: ExpenseStatus;
}

export interface ListExpensesOptions extends PaginationOptions {
  /** Filter by status */
  status?: ExpenseStatus;
}

/**
 * Which ledger to read (R19, 2026-08-18). Server-side, recordAccountExpense
 * writes each expense row to the wallet that was actually debited:
 *   'owner-personal' mode → users/{ownerUid}/wallet/summary/expenses
 *   'workspace' mode      → accounts/{accountId}/wallet/summary/expenses
 * A plain uid string keeps every existing call reading the personal ledger;
 * `{ accountId }` reads the workspace ledger (verified members only, per
 * firestore.rules). Writes (createExpense — the legacy client widget lane)
 * stay personal-only on purpose: workspace debits are server-written.
 */
export type ExpenseStore = string | { accountId: string };

function expensesColFor(store: ExpenseStore) {
  return typeof store === 'string'
    ? Paths.walletExpensesCol(store)
    : Paths.walletExpensesColForAccount(store.accountId);
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Create a new expense record (instant write, no debounce)
 * @returns The created expense with generated ID
 */
export async function createExpense(uid: string, input: CreateExpenseInput): Promise<Expense> {
  addUserScopedWriteGuard(uid, 'createExpense');

  const id = generateWalletItemId();
  const timestamp = Date.now();

  const baseTags = input.tags || [];
  const tags = shouldTagAsTest() && !baseTags.includes('test example')
    ? [...baseTags, 'test example']
    : baseTags;

  const expense: Expense = {
    id,
    timestamp,
    tags,
    summary: input.summary,
    memberId: input.memberId,
    amountUsd: input.amountUsd,
    amountOlv: input.amountOlv,
    olvRateAtTime: input.olvRateAtTime,
    modelId: input.modelId,
    provider: input.provider,
    status: input.status || 'completed',
  };

  const docData = {
    ...toExpenseDoc(expense),
    created_at: serverTimestamp(),
  };

  const ref = Paths.walletExpenseDoc(uid, id);
  await withBackoff(() => setDoc(ref, docData));

  return expense;
}

/**
 * Get a single expense by ID
 */
export async function getExpense(uid: string, id: string): Promise<Expense | null> {
  const ref = Paths.walletExpenseDoc(uid, id);
  const snap = await withBackoff(() => getDoc(ref));
  if (!snap.exists()) return null;
  return fromExpenseDoc(snap.data() as ExpenseDoc);
}

/**
 * List expenses with pagination (newest first)
 */
export async function listExpenses(
  store: ExpenseStore,
  opts?: ListExpensesOptions
): Promise<Page<Expense>> {
  const col = expensesColFor(store);

  // Build query parts
  const parts: any[] = [];

  // Filter by status if provided
  if (opts?.status) {
    parts.push(where('status', '==', opts.status));
  }

  // Always order by timestamp descending (newest first)
  parts.push(orderBy('timestamp', 'desc'));

  // Apply limit
  if (opts?.limit) {
    parts.push(limit(opts.limit));
  }

  // Build base query
  let q = query(col, ...parts);

  // Apply cursor if provided
  if (opts?.startAfter) {
    const { startAfter: fbStartAfter } = await import('firebase/firestore');
    q = query(q, fbStartAfter(opts.startAfter as DocumentSnapshot));
  }

  const snap = await withBackoff(() => getDocs(q));
  const items = snap.docs.map((d) => fromExpenseDoc(d.data() as ExpenseDoc));
  const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;

  return { items, nextCursor };
}

/**
 * Delete an expense (admin only, if ever needed)
 */
export async function deleteExpense(uid: string, id: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'deleteExpense');
  const ref = Paths.walletExpenseDoc(uid, id);
  await withBackoff(() => deleteDoc(ref));
}

/**
 * Delete all expenses with a specific tag
 * @param uid User ID
 * @param tag Tag to filter by (e.g., "test example")
 * @returns Number of deleted expenses
 */
export async function deleteExpensesByTag(uid: string, tag: string): Promise<number> {
  addUserScopedWriteGuard(uid, 'deleteExpensesByTag');

  const col = Paths.walletExpensesCol(uid);
  const q = query(col, where('tags', 'array-contains', tag));
  const snap = await withBackoff(() => getDocs(q));

  let deletedCount = 0;
  for (const doc of snap.docs) {
    await withBackoff(() => deleteDoc(doc.ref));
    deletedCount++;
  }

  return deletedCount;
}

// ============================================================================
// Real-time Listeners
// ============================================================================

/**
 * Subscribe to expenses with real-time updates
 * @param uid User ID
 * @param opts Options including limit and optional status filter
 * @param onData Callback with updated expenses list and cursor
 * @param onError Optional error callback
 * @returns Unsubscribe function
 */
export function onExpensesSnapshot(
  store: ExpenseStore,
  opts: { limit: number; status?: ExpenseStatus },
  onData: (items: Expense[], nextCursor: DocumentSnapshot | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const col = expensesColFor(store);

  // Build query parts
  const parts: any[] = [];

  // Filter by status if provided
  if (opts.status) {
    parts.push(where('status', '==', opts.status));
  }

  // Order by timestamp descending
  parts.push(orderBy('timestamp', 'desc'));

  // Apply limit
  parts.push(limit(opts.limit));

  const q = query(col, ...parts);

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => fromExpenseDoc(d.data() as ExpenseDoc));
      const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
      onData(items, nextCursor);
    },
    (error) => {
      console.error('Expenses snapshot error:', error);
      onError?.(error);
    }
  );
}
